import { graphql } from 'gatsby';
import Link from 'gatsby-link';
import { get, startCase, sortBy } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { STATES } from  '@upsolve/shared';
import { A, H1, H5, H6, HR, Small } from '@upsolve/ui';

import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import * as PAGE_TYPES from '../components/Programmatic/pageTypes';
import Article from '../components/Layout/Article.div';
import AuthorBio from '../components/Layout/AuthorBio';
import AuthorsBlock from '../components/Layout/AuthorsBlock';
import BreadcrumbNav from '../components/Layout/BreadcrumbNav';
import CTASideBySideOfferings from '../components/CTA/CTASideBySideOfferings';
import CitationsList from '../components/Layout/CitationsList';
import ContentAccordian from '../components/Layout/ContentAccordian';
import ContentSectionFooter from '../components/Layout/ContentSectionFooter';
import DisabledReadingPrompt from '../components/CTA/DisabledReadingPrompt';
import HeroFilerCta from '../components/CTA/HeroFilerCta';
import MainFooter from '../components/Layout/Footer'
import MainHeader from '../components/Layout/Header'
import SEO from '../components/SEO/SEO';
import StateGuidesListPanel from '../components/Programmatic/StateGuidesListPanel';
import TableOfContents from '../components/Layout/TableOfContents';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import UpsolveTextBio from '../components/ProofBuilders/UpsolveTextBio';
import VideoInContext from '../components/Media/VideoInContext';
import checkForInlineCta from '../components/Contentful/checkForInlineCta';
import getCitationsFromRichText from '../components/Contentful/getCitationsFromRichText';
import getHeadersFromRichText from '../components/Contentful/getHeadersFromRichText';
import getInlineCTAComponents from '../components/CTA/getInlineCTAComponents';
import getWordCountFromRichText from '../components/Contentful/getWordCountFromRichText';
import pageTypeContentGroups from '../components/analytics/pageTypeContentGroups';
import richTextNodeToComponent from '../components/Contentful/richTextNodeToComponent';
import richTextNodesInSections from '../components/Contentful/richTextNodesInSections';
import trackPageScroll from '../components/analytics/trackPageScroll';
import trackPageView from '../components/analytics/trackPageView';
import trackWindowClosed from '../components/analytics/trackWindowClosed';
import withScrollWatcher from '../components/analytics/withScrollWatcher';
import { Ampify } from '../components/AMP/AmpContext';

const templateProps = {
  pageComponentName: 'PageTemplate',
  pageComponentVersion: 2,
};
const CTAS = getInlineCTAComponents();
const getTrackingProps = (props) => ({
  canonicalUrl: `https://upsolve.org${props.path}`,
  contentCreationMethod: get(props, 'data.page.contentTraits.creationMethod', CONTENT_CREATION_METHODS.WRITTEN),
  contentGroup : get(props, 'data.page.contentTraits.contentGroup') || get(pageTypeContentGroups, get(props, 'data.page.type')),
  contentLocalized: get(props, 'data.page.contentTraits.contentLocalized')
    || get(props, 'data.page.locality') != null,
  contentTopics: get(props, 'data.page.contentTraits.contentTopics', []),
  jsonldTypes: Array.from(new Set((props.jsonLD || []).map(jld => jld['@type']))),
  searchTermTarget: get(props, 'data.page.contentTraits.searchTermTarget'),
  title: props.title || document.title,
  wordCount: props.wordCount,
  ...templateProps,
});

/**
 * PageTemplate
 * - v0: Where we began.
 * - v1: Layout adjustments - condensed top, collapsed TOC, hide CTAs based on screen width
 * - v2: ContentSectionFooter removed, share button at top added, more intuitive Table of Contents
 *
 * @component
 * @version 2
 */
const PageTemplate = (props) => {
  const { scrollWatcher } = props;
  const { content, createdAt, locality, seo, summary, title, type, updatedAt, video } = props.data.page;
  const contentGroup = get(pageTypeContentGroups, type);
  const { amp, city, districts } = props.pageContext;
  const layout = props.data.page.layout || {};
  const wordCount = getWordCountFromRichText(get(props, 'data.page.content.json', []));
  // - Associated Page Data
  const clusterChildren = props.data.childrenPages.edges.map(e => e.node)
    .filter(n => !n.path.includes('/questions/'));
  const clusterSiblings = props.data.siblingPages.edges.map(e => e.node)
    .filter(n => !n.path.includes('/questions/'));
  // - Content
  const sectionHeaders = getHeadersFromRichText(content.json);
  const contentSections = richTextNodesInSections(content.json);
  const citations = getCitationsFromRichText(content.json);
  const allowInlineCta = false;
  const hasInlineCta = checkForInlineCta(content.json);
  const authors = (get(props, 'data.page.authors') || [get(props, 'data.page.author')])
    .filter((author) => author && author.name != null);
  let seoRenderProps = {};

  // On Mount
  useEffect(() => {
    trackPageView(getTrackingProps({ ...props, jsonLD: seoRenderProps.jsonLD, title: seoRenderProps.title, wordCount }));
    const windowClosedTracker = () => trackWindowClosed(getTrackingProps({ ...props, jsonLD: seoRenderProps.jsonLD, title: seoRenderProps.title, wordCount }));
    window.addEventListener('beforeunload', windowClosedTracker);
    return () => {
      window.removeEventListener('beforeunload', windowClosedTracker);
    }
  }, []);

  // - Page Scroll
  const [scrollIntervalCache, setScrollIntervalCache] = useState([0]);
  useEffect(() => {
    if (Math.max(...scrollIntervalCache) < scrollWatcher.scrollInterval) {
      setScrollIntervalCache(scrollIntervalCache.concat(scrollWatcher.scrollInterval));
      trackPageScroll(getTrackingProps({ ...props, jsonLD: seoRenderProps.jsonLD, title: seoRenderProps.title, wordCount }));
    }
  }, [scrollWatcher.scrollInterval]);

  // Comp
  const children = (
    <TopLevelLayout
      showPersistentCta={false}
      persistentCtaText={layout.persistentCtaText}
      showBackground={layout.showBackground}
      backgroundTemplate={layout.backgroundTemplate}
    >
      <SEO
        seoNode={seo}
        pageNode={props.data.page}
        onRender={(props) => { seoRenderProps = props; }}
      />
      <MainHeader
        contentGroup={contentGroup}
        helloBarTemplate={layout.helloBarTemplate}
        helloBarText={layout.helloBarText}
        helloBarUrl={layout.helloBarUrl}
        showHelloBar={layout.showHelloBar}
      />
      <Article>
        <BreadcrumbNav />
        <div className="article__body">
          <div className="article__content">
            <div className="content-hero">
              <header className="content-hero__header" id="top">
                <H1>{title}</H1>
                {type !== PAGE_TYPES.BARE && (
                  <Fragment>
                    <UpsolveTextBio wordCount={wordCount} />
                    <HR />
                  </Fragment>
                )}
              </header>
            </div>
            <div className={type === PAGE_TYPES.BARE ? `content-body full-width` : `content-body`}>
              <article className="content-body__container">
                {get(summary, 'json') != null && (
                  <div className="content-body__lead">
                    <div className="summary">
                      <b>In a Nutshell</b>
                      {Array.prototype.map.call(summary.json.content, (c, i, a) => richTextNodeToComponent(c, i, a, { locality, contentGroup }))}
                    </div>
                  </div>
                )}
                {type !== PAGE_TYPES.BARE && (
                  <AuthorsBlock
                    authors={authors}
                    createdAt={createdAt}
                    updatedAt={updatedAt}
                  />
                )}
                {type !== PAGE_TYPES.BARE && <HR />}
                {sectionHeaders.length > 0 && <TableOfContents sectionHeaders={sectionHeaders} />}
                {video != null && (
                  <section className="content-video">
                    <VideoInContext video={video} />
                  </section>
                )}
                {contentSections.length === 1
                  ? Array.prototype.map.call(contentSections[0], (c, i, a) => richTextNodeToComponent(c, i, a, { locality, contentGroup, districts }))
                  : Array.prototype.map.call(contentSections, (section, sectionIndex, sectionArray) => (
                    <Fragment key={sectionIndex}>
                      <section>
                        {Array.prototype.map.call(section, (c, i, a) => richTextNodeToComponent(c, i, a, { locality, contentGroup, districts }))}
                      </section>
                      {(allowInlineCta && hasInlineCta === false && Math.floor(sectionArray.length * 0.35) === sectionIndex) &&
                        ((CTA) => <CTA key={props.pageContext.inlineCTAOrder[0]} dataTestId="inline-content-cta" />)(CTAS[props.pageContext.inlineCTAOrder[0]])}
                      {(allowInlineCta && hasInlineCta === false && wordCount > 1000 && Math.floor(sectionArray.length * 0.7) === sectionIndex) &&
                        ((CTA) => <CTA key={props.pageContext.inlineCTAOrder[1]} dataTestId="inline-content-cta" />)(CTAS[props.pageContext.inlineCTAOrder[1]])}
                    </Fragment>
                  ))}
                <HR />
                {citations.length > 0 && (
                  <Fragment>
                    <H5 as="h2">Sources:</H5>
                    <CitationsList citations={citations} />
                  </Fragment>
                )}
                <ContentSectionFooter />
                <HR />
                {authors.length > 0 && (
                  <div className="author-bios">
                    <div className="written-by">
                      <Small>Written By:</Small>
                    </div>
                    {authors.map((author) => <AuthorBio key={author.name} author={author} />)}
                    <div className="team">
                      <Link to="/team">Read About the Upsolve Team</Link>
                    </div>
                  </div>
                )}
              </article>
              {type !== PAGE_TYPES.BARE && (
                <aside className="article__sidebar">
                  {type !== PAGE_TYPES.JOB_LISTING && layout.showHeroCta !== false && (
                    <div className="sidebar-hero__cta">
                      {(layout.heroCtaType === 'filer' || layout.heroCtaType == null) && <HeroFilerCta contentGroup={contentGroup} />}
                    </div>
                  )}
                  {type === PAGE_TYPES.JOB_LISTING && (
                    <section>
                      <H6>🗽 Made in New York City</H6>
                      <br />
                      <iframe width="100%" height="240" frameborder="0" style={({ border: '0' })} src="https://www.google.com/maps/embed/v1/place?key=AIzaSyCZ8I8Zyr9Ex3CxhuGEcsHSrIKrBIwURXk&amp;q=Manhattan, NY, United States"></iframe>
                    </section>
                  )}
                </aside>
              )}
            </div>
          </div>
        </div>
        <div className="related-content">
          {type !== PAGE_TYPES.JOB_LISTING && type !== PAGE_TYPES.BARE && (clusterSiblings.length > 0 || clusterChildren.length > 0) && (
            <section>
              <H5>Related Reading</H5>
              <ul>
                {Array.prototype.map.call(sortBy([...clusterChildren, ...clusterSiblings]), (c, cIndex) => (
                  <li key={cIndex}>
                    <a href={c.path}>
                      {get(c, 'context.title') || `${STATES[c.path.slice(1,2)] || ''} ${startCase(c.path.slice(3).replace(/\//g, ' '))}`.trim()}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {type === PAGE_TYPES.STATE_PAGE && (
            <Fragment>
              <section>
                <H5>City Guides</H5>
                <ContentAccordian height="320px">
                  <ul>
                    {[
                      ...props.data.cityPagesHandwritten.edges.map(e => e.node),
                      ...props.data.cityPagesProgrammatic.edges.map(e => e.node).filter(n => n.context.city.population != null),
                      ...props.data.cityPagesProgrammatic.edges.map(e => e.node).filter(n => n.context.city.population == null),
                    ].map((node, nodeIndex) => (
                        <li key={nodeIndex}>
                          <A href={node.path}>
                            Filing Bankruptcy for Free in {get(node, 'context.city.city')
                              ? `${get(node, 'context.city.city')}, ${locality.state.toUpperCase()}`
                              : get(node, 'context.title', '').replace('Filing Bankruptcy in', '')}
                          </A>
                        </li>
                      ))}
                  </ul>
                </ContentAccordian>
              </section>
            </Fragment>
          )}
          {type === PAGE_TYPES.CITY_PAGE && get(city, 'nearbyCities', []).length > 0 && (
            <section>
              <H5>Nearby Cities</H5>
              <ContentAccordian height="320px">
                <ul>
                  {city.nearbyCities.filter((_city) => !!_city).map((_city) => (
                    <li key={_city.cityPagePath}>
                      <A href={_city.cityPagePath}>{_city.formattedAddress}</A>
                    </li>
                  ))}
                </ul>
              </ContentAccordian>
            </section>
          )}
        </div>
      </Article>
      {![PAGE_TYPES.BARE, PAGE_TYPES.JOB_LISTING].includes(type) && <CTASideBySideOfferings contentGroup={contentGroup} />}
      {[PAGE_TYPES.STATE_PAGE, PAGE_TYPES.STATE_BANKRUPTCY_FORMS, PAGE_TYPES.STATE_SUB_PAGE, PAGE_TYPES.STATE_MEANS_TEST].includes(type) && <StateGuidesListPanel />}
      <MainFooter />
      {layout.showDisabledReadingPrompt !== false && (
        <DisabledReadingPrompt
          contentGroup={contentGroup}
          isAllowedToRender={() => (scrollWatcher.scrollPosition > (scrollWatcher.documentHeight / 2))}
        />
      )}
    </TopLevelLayout>
  );

  return amp === true
    ? <Ampify>{children}</Ampify>
    : children;
};

// TODO: Move SEO fragment to a StaticQuery within SEO.jsx
// query PageByPath($path: String!, $childrenPathRegex: String, $siblingPathRegex: String) {
export const pageQuery = graphql`
  query PageByPath(
    $path: String,
    $canonicalPath: String!,
    $childrenPathRegex: String, $excludedChildrenPageTypes: [String], $excludedChildrenTemplates: String
    $siblingPathRegex: String, $excludedSiblingPageTypes: [String], $excludedSiblingTemplates: String
  ) {
    page: contentfulPage(path: { eq: $canonicalPath }) {
      path
      title
      type
      content {
        json
      }
      summary {
        json
      }
      author {
        ...AuthorNode
      }
      createdAt
      updatedAt
      locality {
        level
        state
        city
      }
      contentTraits {
        contentCreationMethod
        contentGroup
        contentLocalized
        contentTopics
        searchTermTarget
      }
      layout {
        showHelloBar
        showPersistentCta
        showHeroCta
        showBackground
        showDisabledReadingPrompt
        helloBarTemplate
        helloBarText
        helloBarUrl
        persistentCtaText
        heroCtaType
        backgroundTemplate
      }
      video {
        ...VideoNode
      }
      seo {
        ...SeoNode
      }
    }

    childrenPages: allSitePage(
      filter: {
        path: {
          regex: $childrenPathRegex
          ne: $canonicalPath
        }
        component: {
          regex: $excludedChildrenTemplates
        }
        context: {
          amp: { ne: true }
          type: { nin: $excludedChildrenPageTypes }
        }
      }
    ) {
      edges {
        node {
          path
          component
          context {
            title
          }
        }
      }
    }

    siblingPages: allSitePage(
      filter: {
        path: {
          regex: $siblingPathRegex
          ne: $canonicalPath
        },
        component: {
          regex: $excludedSiblingTemplates
        }
        context: {
          amp: { ne: true }
          type: { nin: $excludedSiblingPageTypes }
        }
      }
    ) {
      edges {
        node {
          path
          component
          context {
            title
          }
        }
      }
    }

    cityPagesHandwritten: allSitePage(
      filter: {
        path: {
          regex: $path
        }
        component: {
          regex: "/PageTemplate/"
        }
        context: {
          type: {
            eq: "cityPage"
          }
        }
      }
    ) {
      edges {
        node {
          path
          context {
            title
          }
        }
      }
    }

    cityPagesProgrammatic: allSitePage(
      filter: {
        path: {
          regex: $path
        }
        component: {
          regex: "/CityPage/"
        }
      }
      sort: {
        fields: context___city___population
        order: DESC
      }
    ) {
      edges {
        node {
          path
          context {
            city {
              city
              population
            }
          }
        }
      }
    }
  }
`;


export default withScrollWatcher(PageTemplate);
